import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, Type, ImageIcon, Crown, RotateCcw, Save, 
  Rocket, Upload, Trash2, Check, Minus, Plus, Film, ArrowUp, ArrowDown 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore, useAppearanceStore, useLanguageStore } from '../store';
import { SEO } from '../components/SEO';
import AdminHeader from '../components/admin/AdminHeader';
import { toast } from 'sonner';
import { uploadToCloudinary } from './AdminSettingsPage';
import { useTranslation } from '../i18n/translations';

import {
  DEFAULT_APPEARANCE, CURATED_FONTS, COLOR_GROUPS, IMAGE_SLOTS, PRESETS, HERO_SPEED_PRESETS,
  applyAppearanceToDom, removeAppearanceStyle, resolveSlot,
} from '../lib/appearance';

const API = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_STYLE_ID = 'fedyk-appearance-preview';
const PREVIEW_FONT_ID = 'fedyk-fonts-preview';

const TABS = [
  { key: 'colors', label: 'Colors', icon: Palette },
  { key: 'typography', label: 'Typography', icon: Type },
  { key: 'logo', label: 'Logo & Settings', icon: Crown },
  { key: 'hero', label: 'Hero Slider', icon: Film },
  { key: 'images', label: 'Images', icon: ImageIcon },
];

const ColorRow = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-sm text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2 border border-border px-1 py-0.5">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-7 h-7 cursor-pointer border-0 bg-transparent p-0" data-testid={`${testid}-picker`} />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="w-20 text-xs font-mono bg-transparent focus:outline-none uppercase" data-testid={`${testid}-hex`} />
    </div>
  </div>
);

const FontSelect = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-border text-sm appearance-none cursor-pointer"
      style={{ color: '#000', backgroundColor: '#fff' }}
      data-testid={testid}
    >
      {CURATED_FONTS.map((f) => (
        <option key={f.name} value={f.name} style={{ color: '#000', backgroundColor: '#fff' }}>
          {f.name} · {f.category}
        </option>
      ))}
    </select>
  </div>
);

const BoldToggle = ({ active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={active ? 'Bold: on' : 'Bold: off'}
    className={`w-9 h-9 flex items-center justify-center border text-sm font-bold transition-colors ${active ? 'bg-foreground text-background border-foreground' : 'bg-background text-muted-foreground border-border hover:text-foreground'}`}
    data-testid={testid}
  >
    B
  </button>
);

const SlotUploader = ({ slot, current, isOverride, onUpload, onReset, uploading }) => (
  <div className="border border-border p-3" data-testid={`image-slot-${slot.key}`}>
    <div className="aspect-[4/3] overflow-hidden bg-muted mb-3 border border-border">
      {current ? <img src={current} alt={slot.label} className="w-full h-full object-cover" /> : null}
    </div>
    <p className="text-sm font-light truncate">{slot.label}</p>
    <p className="text-[10px] text-muted-foreground mb-3">{isOverride ? 'Custom override' : 'Using default'}</p>
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="flex-1" disabled={uploading} onClick={onUpload} data-testid={`image-slot-${slot.key}-upload`}>
        <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />{uploading ? 'Uploading…' : 'Replace'}
      </Button>
      {isOverride && (
        <Button variant="outline" size="sm" className="text-destructive" onClick={onReset} data-testid={`image-slot-${slot.key}-reset`}>
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Button>
      )}
    </div>
  </div>
);

const LogoWordmark = ({ logo, className = "" }) => {
  if (!logo) return null;
  const w1 = logo.word1 ?? 'FEDYK';
  const w2 = logo.word2 ?? 'Furniture';

  const getFont = (fontName) => {
    if (!fontName || fontName === 'inherit') return undefined;
    return fontName.includes(' ') ? `"${fontName}", sans-serif` : `${fontName}, sans-serif`;
  };

  return (
    <div className={className}>
      <span 
        className={logo.word1_bold ? "font-bold" : "font-light"}
        style={logo.word1_font ? { fontFamily: getFont(logo.word1_font) } : undefined}
      >
        {w1}
      </span>
      {" "}
      {w2 ? (
        <span 
          className={logo.word2_bold ? "font-bold" : "font-light"}
          style={logo.word2_font ? { fontFamily: getFont(logo.word2_font) } : undefined}
        >
          {w2}
        </span>
      ) : null}
    </div>
  );
};

const AdminAppearancePage = () => {
  const navigate = useNavigate();
  const tokenFromHook = useAuthStore((s) => s.token);
  const userFromHook = useAuthStore((s) => s.user);
  
  const refreshAppearance = useAppearanceStore((s) => s.fetchAppearance);
  
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const [hydrated, setHydrated] = useState(false);
  const [working, setWorking] = useState(null);
  const [publishedAt, setPublishedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('colors');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const slotInputRef = useRef(null);
  const pendingSlot = useRef(null);

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => { const id = setTimeout(() => setHydrated(true), 300); return () => clearTimeout(id); }, []);
  
  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') navigate('/admin', { replace: true });
  }, [hydrated, navigate, user]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/appearance`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (alive) { setWorking(data.draft || DEFAULT_APPEARANCE); setPublishedAt(data.updated_at); setLoading(false); }
      } catch (e) { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => { if (working) applyAppearanceToDom(working, PREVIEW_STYLE_ID, PREVIEW_FONT_ID); }, [working]);
  useEffect(() => () => removeAppearanceStyle(PREVIEW_STYLE_ID, PREVIEW_FONT_ID), []);

  if (!hydrated || loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground font-light tracking-wide">Loading engine map…</div>;
  if (!user || user.role !== 'admin' || !working) return null;

  const setColor = (key, val) => setWorking((w) => ({ ...w, colors: { ...w.colors, [key]: val } }));
  const setTypo = (key, val) => setWorking((w) => ({ ...w, typography: { ...w.typography, [key]: val } }));
  const setLogo = (patch) => setWorking((w) => ({ ...w, logo: { ...w.logo, ...patch } }));
  
  const setImage = (slotKey, obj) => setWorking((w) => {
    const images = { ...(w.images || {}) };
    if (obj) images[slotKey] = obj; else delete images[slotKey];
    return { ...w, images };
  });
  
  const setHero = (patch) => setWorking((w) => ({ ...w, hero: { ...(w.hero || DEFAULT_APPEARANCE.hero), ...patch } }));
  const heroSlides = working.hero?.slides || [];
  
  const moveSlide = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= heroSlides.length) return;
    const next = [...heroSlides];
    [next[i], next[j]] = [next[j], next[i]];
    setHero({ slides: next });
  };
  const deleteSlide = (i) => setHero({ slides: heroSlides.filter((_, idx) => idx !== i) });

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/appearance/draft`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(working),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Draft configuration saved');
    } catch (e) { toast.error(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const publish = async () => {
    if (!window.confirm('Publish these changes to the LIVE public website?')) return;
    setPublishing(true);
    try {
      const res = await fetch(`${API}/api/admin/appearance/publish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(working),
      });
      if (!res.ok) throw new Error('Publish failed');
      
      refreshAppearance();
      window.dispatchEvent(new CustomEvent('site-appearance-updated'));
      
      toast.success('Published to the live site successfully');
      setPublishedAt(new Date().toISOString());
    } catch (e) { toast.error(e.message || 'Publish failed'); } finally { setPublishing(false); }
  };

  const resetDraft = async () => {
    if (!window.confirm('Reset the draft to brand defaults? (This does not change the live site until you publish.)')) return;
    try {
      const res = await fetch(`${API}/api/admin/appearance/reset`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setWorking(data.draft || DEFAULT_APPEARANCE);
      toast.success('Draft reset to defaults');
    } catch (e) { toast.error('Reset failed'); }
  };

  const triggerSlotUpload = (slotKey) => { pendingSlot.current = slotKey; slotInputRef.current?.click(); };

  const handleSlotFile = async (file) => {
    const slotKey = pendingSlot.current;
    if (!file || !slotKey) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp', 'svg'].includes(ext)) { toast.error('Image must be JPG, PNG, WebP or SVG'); return; }
    setUploadingKey(slotKey);
    try {
      const up = await uploadToCloudinary(file, token, 'site', 'image');
      if (slotKey === 'logo_header') setLogo({ header_image_url: up.secure_url, header_image_public_id: up.public_id });
      else if (slotKey === 'logo_footer') setLogo({ footer_image_url: up.secure_url, footer_image_public_id: up.public_id });
      else if (slotKey === 'hero_add') setHero({ slides: [...(working.hero?.slides || []), { url: up.secure_url, public_id: up.public_id }] });
      else if (slotKey.startsWith('hero_replace_')) {
        const i = parseInt(slotKey.replace('hero_replace_', ''), 10);
        const next = [...(working.hero?.slides || [])];
        next[i] = { url: up.secure_url, public_id: up.public_id };
        setHero({ slides: next });
      } else setImage(slotKey, { url: up.secure_url, public_id: up.public_id });
      toast.success('Image template uploaded');
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setUploadingKey(null); pendingSlot.current = null; if (slotInputRef.current) slotInputRef.current.value = ''; }
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      <SEO title="Appearance · Admin" description="Site appearance settings" path="admin/appearance" />
      <input ref={slotInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleSlotFile(e.target.files?.[0])} data-testid="appearance-file-input" />

      <AdminHeader active="appearance" />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-8">
          <p className="text-caption text-muted-foreground mb-3">CMS · Appearance</p>
          <h1 className="text-4xl md:text-5xl font-extralight tracking-tight">Appearance</h1>
          <p className="text-muted-foreground font-light mt-3 max-w-xl leading-relaxed">
            Total visual control — colors, typography, logo and every site image. Changes preview live here; click <strong>Publish</strong> to push them to the public site.
          </p>
        </div>

        {/* Tabs навигации */}
        <div className="flex items-center gap-1 border-b border-border mb-8 overflow-x-auto" data-testid="appearance-tabs">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button key={tb.key} onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === tb.key ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                data-testid={`appearance-tab-${tb.key}`}>
                <Icon className="w-4 h-4" strokeWidth={1.5} />{tb.label}
              </button>
            );
          })}
        </div>

        {/* СЕКЦИЯ ЦВЕТОВЫХ ПАЛИТР */}
        {tab === 'colors' && (
          <div className="space-y-8" data-testid="appearance-colors">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Presets</p>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button key={p.key} onClick={() => setWorking((w) => ({ ...w, colors: { ...p.colors } }))}
                    className="flex items-center gap-2 px-3 py-2 border border-border text-sm hover:border-foreground transition-colors" data-testid={`preset-${p.key}`}>
                    <span className="flex -space-x-1">
                      {['background', 'foreground', 'button_bg'].map((k) => (
                        <span key={k} className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: p.colors[k] }} />
                      ))}
                    </span>
                    {p.name}
                  </button>
                ))}
                <button onClick={() => setWorking((w) => ({ ...w, colors: { ...DEFAULT_APPEARANCE.colors } }))}
                  className="flex items-center gap-2 px-3 py-2 border border-border text-sm text-destructive hover:border-destructive transition-colors" data-testid="reset-colors">
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />Reset to brand
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
              {COLOR_GROUPS.map((g) => (
                <div key={g.group} className="border border-border p-5">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">{g.group}</p>
                  {g.tokens.map((tk) => (
                    <ColorRow key={tk.key} label={tk.label} value={working?.colors?.[tk.key] || '#FFFFFF'} onChange={(v) => setColor(tk.key, v)} testid={`color-${tk.key}`} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* СЕКЦИЯ ШРИФТОВ */}
        {tab === 'typography' && (
          <div className="space-y-8" data-testid="appearance-typography">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FontSelect label="Heading font" value={working.typography.heading_font} onChange={(v) => setTypo('heading_font', v)} testid="font-heading-select" />
              <FontSelect label="Body font" value={working.typography.body_font} onChange={(v) => setTypo('body_font', v)} testid="font-body-select" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Base size scale · <span data-testid="font-scale-value">{Math.round(working.typography.base_scale * 100)}%</span></label>
              <input type="range" min="0.85" max="1.25" step="0.05" value={working.typography.base_scale}
                onChange={(e) => setTypo('base_scale', parseFloat(e.target.value))} className="w-full max-w-md" data-testid="font-scale-slider" />
            </div>
            <div className="border border-border p-8 space-y-4" data-testid="typography-preview">
              <p className="text-caption text-muted-foreground">Live preview</p>
              <h2 className="text-h2" data-testid="typography-preview-heading">The art of bespoke furniture</h2>
              <p className="text-body-lg">FEDYK Furniture crafts timeless interiors with precision and warmth — every line, every grain considered.</p>
              <Button className="btn-primary">
                {t?.hero?.cta || 'Discover the concept'}
              </Button>
            </div>
          </div>
        )}

        {/* СЕКЦИЯ ЛОГОТИПА И ГЛОБАЛЬНЫХ НАСТРОЕК */}
        {tab === 'logo' && (
          <div className="space-y-8" data-testid="appearance-logo">
            <div className="flex items-center gap-3">
              {['text', 'image'].map((m) => (
                <button key={m} onClick={() => setLogo({ mode: m })}
                  className={`px-4 py-2 border text-sm capitalize transition-colors ${working.logo.mode === m ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid={`logo-mode-${m}`}>
                  {working.logo.mode === m && <Check className="w-3.5 h-3.5 mr-1.5 inline" strokeWidth={2} />} {m} logo
                </button>
              ))}
            </div>

            {working.logo.mode === 'text' ? (
              <div className="max-w-md space-y-6">
                {/* FIRST WORD */}
                <div className="border border-border/60 p-4 bg-muted/10 space-y-3">
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">First word text</label>
                    <div className="flex items-center gap-2">
                      <input type="text" value={working.logo.word1 || ''} onChange={(e) => setLogo({ word1: e.target.value })}
                        className="flex-1 px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground" data-testid="logo-word1-input" />
                      <BoldToggle active={working.logo.word1_bold} onClick={() => setLogo({ word1_bold: !working.logo.word1_bold })} testid="logo-word1-bold" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-1">First word font</label>
                    <select
                      value={working.logo.word1_font || ''}
                      onChange={(e) => setLogo({ word1_font: e.target.value })}
                      className="w-full px-3 py-2 border border-border text-xs bg-background focus:outline-none cursor-pointer"
                    >
                      <option value="">Site Default Font (Inherit)</option>
                      <option value="EuropeExtendedC">EuropeExtendedC</option>
                      <option value="BankGothic Lt BT">BankGothic Lt BT</option>
                    </select>
                  </div>
                </div>

                {/* SECOND WORD */}
                <div className="border border-border/60 p-4 bg-muted/10 space-y-3">
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Second word text</label>
                    <div className="flex items-center gap-2">
                      <input type="text" value={working.logo.word2 || ''} onChange={(e) => setLogo({ word2: e.target.value })}
                        className="flex-1 px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground" data-testid="logo-word2-input" />
                      <BoldToggle active={working.logo.word2_bold} onClick={() => setLogo({ word2_bold: !working.logo.word2_bold })} testid="logo-word2-bold" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-1">Second word font</label>
                    <select
                      value={working.logo.word2_font || ''}
                      onChange={(e) => setLogo({ word2_font: e.target.value, logo_font_v2: e.target.value })}
                      className="w-full px-3 py-2 border border-border text-xs bg-background focus:outline-none cursor-pointer"
                    >
                      <option value="">Site Default Font (Inherit)</option>
                      <option value="BankGothic Lt BT">BankGothic Lt BT</option>
                      <option value="EuropeExtendedC">EuropeExtendedC</option>
                    </select>
                  </div>
                </div>

                <p className="text-[10px] text-muted-foreground">Toggle <strong>B</strong> to switch each word between bold and light. Customize independent font family stacks seamlessly.</p>
                <div className="mt-2 border border-border p-6 bg-white">
                  <LogoWordmark logo={working.logo} className="font-heading text-2xl tracking-tight text-[#0A0A0A]" testid="logo-preview-text" />
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="border border-border p-5 max-w-xl">
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Logo size · <span data-testid="logo-scale-value">{Math.round((working.logo.image_scale || 1) * 100)}%</span></label>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => setLogo({ image_scale: Math.max(0.5, Math.round(((working.logo.image_scale || 1) - 0.1) * 10) / 10) })} data-testid="logo-scale-decrease"><Minus className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                    <input type="range" min="0.5" max="2.5" step="0.1" value={working.logo.image_scale || 1} onChange={(e) => setLogo({ image_scale: parseFloat(e.target.value) })} className="flex-1" data-testid="logo-scale-slider" />
                    <Button variant="outline" size="sm" onClick={() => setLogo({ image_scale: Math.min(2.5, Math.round(((working.logo.image_scale || 1) + 0.1) * 10) / 10) })} data-testid="logo-scale-increase"><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                    <Button variant="outline" size="sm" onClick={() => setLogo({ image_scale: 1.0 })} data-testid="logo-scale-reset"><RotateCcw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Standard</Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Adjust how large the logo image appears in the site header & footer.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border border-border p-5">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Header logo (light background)</p>
                    <div className="bg-white border border-border p-6 flex items-center justify-center min-h-[80px] mb-3">
                      {working.logo.header_image_url ? <img src={working.logo.header_image_url} alt="header logo" className="w-auto object-contain" style={{ height: `${(working.logo.image_scale || 1) * 2.5}rem` }} data-testid="logo-preview-image" /> : <span className="text-xs text-muted-foreground">No image</span>}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" disabled={uploadingKey === 'logo_header'} onClick={() => triggerSlotUpload('logo_header')} data-testid="logo-header-upload">
                        <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />{uploadingKey === 'logo_header' ? 'Uploading…' : 'Upload'}
                      </Button>
                      {working.logo.header_image_url && <Button variant="outline" size="sm" className="text-destructive" onClick={() => setLogo({ header_image_url: null, header_image_public_id: null })} data-testid="logo-header-remove"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>}
                    </div>
                  </div>
                  <div className="border border-border p-5">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Footer logo (dark background)</p>
                    <div className="bg-neutral-900 border border-border p-6 flex items-center justify-center min-h-[80px] mb-3">
                      {(working.logo.footer_image_url || working.logo.header_image_url) ? <img src={working.logo.footer_image_url || working.logo.header_image_url} alt="footer logo" className="w-auto object-contain" style={{ height: `${(working.logo.image_scale || 1) * 2.5}rem` }} /> : <span className="text-xs text-neutral-500">No image</span>}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" disabled={uploadingKey === 'logo_footer'} onClick={() => triggerSlotUpload('logo_footer')} data-testid="logo-footer-upload">
                        <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />{uploadingKey === 'logo_footer' ? 'Uploading…' : 'Upload'}
                      </Button>
                      {working.logo.footer_image_url && <Button variant="outline" size="sm" className="text-destructive" onClick={() => setLogo({ footer_image_url: null, footer_image_public_id: null })} data-testid="logo-footer-remove"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* БЛОК 1: ГЛОБАЛЬНЫЕ КОНТАКТЫ И СОЦИАЛЬНЫЕ СЕТИ */}
            <div className="border border-border p-5 max-w-xl bg-neutral-50/50 space-y-6 mt-8">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-black font-semibold mb-1">Global Contact & Social Settings</p>
                <p className="text-[11px] text-muted-foreground font-light">Manage key metadata and social network profiles rendered across the public footer dynamically.</p>
              </div>
              
              <div className="space-y-1.5 border-b border-border pb-4">
                <label className="text-xs font-medium text-foreground">Footer Support Email</label>
                <input
                  type="email"
                  value={working.brand_email || ''}
                  onChange={(e) => setWorking((w) => ({ ...w, brand_email: e.target.value.trim() }))}
                  placeholder="info@fedykfurniture.com"
                  className="w-full px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground rounded-none"
                  data-testid="brand-email-input"
                />
              </div>

              {/* Секция ссылок социальных сетей */}
              <div className="space-y-4">
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-medium">Social Media Links</p>
                
                {/* Facebook */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="fb-enable"
                      checked={working.facebook_enabled || false} 
                      onChange={(e) => setWorking((w) => ({ ...w, facebook_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-black cursor-pointer"
                    />
                    <label htmlFor="fb-enable" className="text-xs font-medium text-foreground cursor-pointer select-none">Enable Facebook</label>
                  </div>
                  {working.facebook_enabled && (
                    <input
                      type="url"
                      value={working.facebook_url || ''}
                      onChange={(e) => setWorking((w) => ({ ...w, facebook_url: e.target.value.trim() }))}
                      placeholder="https://facebook.com/fedykfurniture"
                      className="w-full px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground rounded-none animate-in fade-in duration-200"
                    />
                  )}
                </div>

                {/* Instagram */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="ig-enable"
                      checked={working.instagram_enabled || false} 
                      onChange={(e) => setWorking((w) => ({ ...w, instagram_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-black cursor-pointer"
                    />
                    <label htmlFor="ig-enable" className="text-xs font-medium text-foreground cursor-pointer select-none">Enable Instagram</label>
                  </div>
                  {working.instagram_enabled && (
                    <input
                      type="url"
                      value={working.instagram_url || ''}
                      onChange={(e) => setWorking((w) => ({ ...w, instagram_url: e.target.value.trim() }))}
                      placeholder="https://instagram.com/fedykfurniture"
                      className="w-full px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground rounded-none animate-in fade-in duration-200"
                    />
                  )}
                </div>

                {/* LinkedIn */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="li-enable"
                      checked={working.linkedin_enabled || false} 
                      onChange={(e) => setWorking((w) => ({ ...w, linkedin_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-black cursor-pointer"
                    />
                    <label htmlFor="li-enable" className="text-xs font-medium text-foreground cursor-pointer select-none">Enable LinkedIn</label>
                  </div>
                  {working.linkedin_enabled && (
                    <input
                      type="url"
                      value={working.linkedin_url || ''}
                      onChange={(e) => setWorking((w) => ({ ...w, linkedin_url: e.target.value.trim() }))}
                      placeholder="https://linkedin.com/company/fedykfurniture"
                      className="w-full px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground rounded-none animate-in fade-in duration-200"
                    />
                  )}
                </div>
              </div>
            </div> {/* ИСПРАВЛЕНО: Закрыли родительский контейнер Global Contact, изолировав его от чат-матрицы */}

            {/* БЛОК 2: МУЛЬТИРЕГИОНАЛЬНАЯ ПОДДЕРЖКА ЧАТОВ */}
            <div className="border border-border p-5 max-w-xl bg-neutral-50/50 space-y-6 mt-8">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-black font-semibold mb-1">Multi-Regional Chat & Support Matrix</div>
                <div className="text-[11px] text-muted-foreground font-light">Configure distinct regional routing for Phone, WhatsApp and Telegram channels. Empty fields automatically hide countries from frontend selection.</div>
              </div>
  
              {/* ИСПРАВЛЕНО: Полностью удален дублирующий инпут Footer Support Email, так как он уже настроен в блоке выше */}

              {/* 1. ПРЯМЫЕ ЗВОНКИ (PHONE CALLS) */}
              <div className="space-y-3 border-b border-border pb-4">
                <div className="text-xs font-semibold text-foreground uppercase tracking-wider">1. Direct Phone Calls</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Romania (RO)</label>
                    <input type="text" placeholder="+40..." value={working.chat_phone_ro || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_phone_ro: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Belgium (BE)</label>
                    <input type="text" placeholder="+32..." value={working.chat_phone_be || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_phone_be: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Ireland (IE)</label>
                    <input type="text" placeholder="+353..." value={working.chat_phone_ie || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_phone_ie: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                </div>
              </div>

              {/* 2. WHATSAPP */}
              <div className="space-y-3 border-b border-border pb-4">
                <div className="text-xs font-semibold text-foreground uppercase tracking-wider">2. WhatsApp Numbers</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Romania (RO)</label>
                    <input type="text" placeholder="+40..." value={working.chat_whatsapp_ro || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_whatsapp_ro: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Belgium (BE)</label>
                    <input type="text" placeholder="+32..." value={working.chat_whatsapp_be || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_whatsapp_be: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Ireland (IE)</label>
                    <input type="text" placeholder="+353..." value={working.chat_whatsapp_ie || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_whatsapp_ie: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                </div>
              </div>

              {/* 3. TELEGRAM */}
              <div className="space-y-3">
                <div className="text-xs font-semibold text-foreground uppercase tracking-wider">3. Telegram Usernames</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Romania (RO)</label>
                    <input type="text" placeholder="fedyk_ro" value={working.chat_telegram_ro || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_telegram_ro: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Belgium (BE)</label>
                    <input type="text" placeholder="fedyk_be" value={working.chat_telegram_be || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_telegram_be: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted-foreground uppercase mb-1">Ireland (IE)</label>
                    <input type="text" placeholder="fedyk_ie" value={working.chat_telegram_ie || ''} onChange={(e) => setWorking((w) => ({ ...w, chat_telegram_ie: e.target.value.trim() }))} className="w-full px-2 py-1.5 border border-border text-xs rounded-none bg-background focus:outline-none focus:border-foreground" />
                  </div>
                </div>
              </div>

            </div>

            <p className="text-[10px] text-muted-foreground italic leading-relaxed mt-4">
              * Icons will automatically become visible in the public footer only if their respective checkboxes are checked AND a valid URL link is provided.
            </p>
          </div>
        )}

        {/* СЕКЦИЯ ГЛАВНОГО СЛАЙДЕРА */}
        {tab === 'hero' && (
          <div className="space-y-10" data-testid="appearance-hero">
            <div className="border border-border p-5 max-w-2xl space-y-4" data-testid="appearance-hero-video">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-1">Background video</p>
                <p className="text-xs text-muted-foreground font-light">Optional. When an MP4 URL is set, it replaces the slide carousel on the homepage hero. Leave empty to keep the image slides.</p>
              </div>
              {[
                { key: 'video_url', label: 'Video URL (MP4, web-optimised)', ph: 'https://…/hero.mp4' },
                { key: 'video_webm_url', label: 'Video URL (WEBM, optional)', ph: 'https://…/hero.webm' },
                { key: 'poster_url', label: 'Poster / fallback image (high-res)', ph: 'https://…/poster.jpg' },
                { key: 'mobile_image', label: 'Mobile / slow-connection image', ph: 'https://…/mobile.jpg' },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-xs text-muted-foreground mb-1.5">{f.label}</label>
                  <input
                    type="url"
                    value={working.hero?.[f.key] || ''}
                    onChange={(e) => setHero({ [f.key]: e.target.value.trim() })}
                    placeholder={f.ph}
                    className="w-full px-3 py-2 border border-border bg-background text-sm focus:outline-none focus:border-foreground"
                    data-testid={`hero-${f.key}`}
                  />
                </div>
              ))}
              {working.hero?.video_url && (
                <button
                  type="button"
                  onClick={() => setHero({ video_url: '', video_webm_url: '' })}
                  className="text-[10px] tracking-[0.2em] uppercase text-destructive hover:underline"
                  data-testid="hero-video-clear"
                >
                  Remove video · revert to slides
                </button>
              )}
            </div>

            <div className="border border-border p-5 max-w-2xl">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">Transition speed</p>
              <div className="flex flex-wrap items-center gap-2">
                {HERO_SPEED_PRESETS.map((p) => (
                  <button key={p.key} onClick={() => setHero({ interval_ms: p.ms })}
                    className={`px-4 py-2 border text-sm transition-colors ${(working.hero?.interval_ms || 6000) === p.ms ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    data-testid={`hero-speed-${p.key}`}>
                    {p.label} <span className="opacity-60">· {p.ms / 1000}s</span>
                  </button>
                ))}
                <div className="flex items-center gap-2 ml-2">
                  <span className="text-sm text-muted-foreground">Custom</span>
                  <input type="number" min="1.5" max="20" step="0.5"
                    value={(working.hero?.interval_ms || 6000) / 1000}
                    onChange={(e) => setHero({ interval_ms: Math.round((parseFloat(e.target.value) || 6) * 1000) })}
                    className="w-20 px-2 py-1.5 border border-border bg-background text-sm focus:outline-none focus:border-foreground" data-testid="hero-speed-custom" />
                  <span className="text-sm text-muted-foreground">sec</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Slides · {heroSlides.length}</p>
                <Button variant="outline" size="sm" disabled={uploadingKey === 'hero_add'} onClick={() => triggerSlotUpload('hero_add')} data-testid="hero-add-slide">
                  <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />{uploadingKey === 'hero_add' ? 'Uploading…' : 'Add slide'}
                </Button>
              </div>
              {heroSlides.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">No slides yet — add one to build the homepage hero carousel.</p>
              ) : (
                <div className="space-y-3">
                  {heroSlides.map((slide, i) => (
                    <div key={`${slide.url}-${i}`} className="flex items-center gap-4 border border-border p-3" data-testid={`hero-slide-${i}`}>
                      <div className="w-28 h-16 overflow-hidden bg-muted shrink-0 border border-border">
                        {slide.url ? <img src={slide.url} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">Slide {i + 1}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{slide.url}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm" disabled={i === 0} onClick={() => moveSlide(i, -1)} data-testid={`hero-slide-${i}-up`}><ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                        <Button variant="outline" size="sm" disabled={i === heroSlides.length - 1} onClick={() => moveSlide(i, 1)} data-testid={`hero-slide-${i}-down`}><ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                        <Button variant="outline" size="sm" disabled={uploadingKey === `hero_replace_${i}`} onClick={() => triggerSlotUpload(`hero_replace_${i}`)} data-testid={`hero-slide-${i}-replace`}><Upload className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                        <Button variant="outline" size="sm" className="text-destructive" onClick={() => deleteSlide(i)} data-testid={`hero-slide-${i}-delete`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* СЕКЦИЯ СТАТИЧЕСКИХ МЕДИА-СЛОТОВ */}
        {tab === 'images' && (
          <div className="space-y-10" data-testid="appearance-images">
            {['Homepage', 'Mega Menu', 'About', 'Auth', 'B2B'].map((grp) => (
              <div key={grp}>
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">{grp}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {IMAGE_SLOTS.filter((s) => s.group === grp && !['home_collection_wardrobes', 'home_collection_doors', 'home_collection_kitchens'].includes(s.key)).map((slot) => {
                    const override = working.images?.[slot.key];
                    return (
                      <SlotUploader key={slot.key} slot={slot}
                        current={resolveSlot(working.images, slot.key, slot.default_url)}
                        isOverride={Boolean(override && override.url)}
                        uploading={uploadingKey === slot.key}
                        onUpload={() => triggerSlotUpload(slot.key)}
                        onReset={() => setImage(slot.key, null)} />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Нижняя закрепленная панель действий */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-white/95 backdrop-blur-md z-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
          <Button variant="outline" size="sm" onClick={resetDraft} data-testid="appearance-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Reset draft
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="appearance-save-btn">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />{saving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button onClick={publish} disabled={publishing} className="btn-primary" data-testid="appearance-publish-btn">
              <Rocket className="w-4 h-4 mr-2" strokeWidth={1.5} />{publishing ? 'Publishing…' : 'Publish to Live'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAppearancePage;
